import { PublicLayout } from "../../components/layout/PublicLayout";
import { MapPin, Mail, ShieldCheck, Scale, Lock, UserCheck } from "lucide-react";

const RIGHTS = [
  "Right to be informed about how your data is used",
  "Right of access to the personal data we hold about you",
  "Right to rectification of inaccurate or incomplete data",
  "Right to erasure ('right to be forgotten')",
  "Right to restrict or object to processing",
  "Right to data portability",
];

export default function Contact() {
  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="text-xs uppercase tracking-[0.25em] text-[#C79A3B] font-semibold">Get in touch</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#101820] mt-2">Contact & Data Protection</h1>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="bg-white rounded-xl border border-[#C79A3B]/30 p-7 shadow-md">
            <div className="w-11 h-11 rounded-lg bg-[#101820] grid place-items-center mb-4"><MapPin className="text-[#C79A3B]" /></div>
            <h2 className="font-heading font-bold text-lg text-[#101820]">KERA · Ireland</h2>
            <address className="not-italic text-sm text-muted-foreground leading-relaxed mt-2">
              Unit 7, The Business Centre,<br />618 Northwest Logistics Park,<br />Ballycoolin, Dublin 15,<br />Ireland, D15V00Y
            </address>
            <div className="flex items-center gap-2 mt-4 text-sm text-[#101820]/80" data-testid="contact-email">
              <Mail className="h-4 w-4 text-[#C79A3B]" /> <a href="mailto:careers@kerafoods.ie" className="hover:text-[#8a6a1e]">careers@kerafoods.ie</a>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#C79A3B]/30 p-7 shadow-md">
            <div className="w-11 h-11 rounded-lg bg-[#101820] grid place-items-center mb-4"><ShieldCheck className="text-[#C79A3B]" /></div>
            <h2 className="font-heading font-bold text-lg text-[#101820]">Recruitment Privacy</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              KERA processes recruitment data solely to assess your suitability for roles you apply to. Our AI Candidate Match
              Score is a decision-support tool only — final hiring decisions are always made by authorised KERA personnel, and
              we never score protected characteristics.
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm text-[#101820]/80">
              <Mail className="h-4 w-4 text-[#C79A3B]" /> Data queries: <a href="mailto:careers@kerafoods.ie" className="hover:text-[#8a6a1e]">careers@kerafoods.ie</a>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-xl border border-[#C79A3B]/30 p-7 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-lg bg-[#101820] grid place-items-center"><Scale className="text-[#C79A3B]" /></div>
            <h2 className="font-heading font-bold text-lg text-[#101820]">EU / Ireland GDPR & Data Protection Statement</h2>
          </div>
          <div className="space-y-4 text-sm text-[#101820]/80 leading-relaxed">
            <p>
              This service processes personal data in accordance with the <strong>EU General Data Protection Regulation
              (Regulation (EU) 2016/679)</strong> and the <strong>Irish Data Protection Act 2018</strong>. KERA acts as the
              <strong> data controller</strong> for personal information you submit through this careers portal.
            </p>
            <div>
              <h3 className="font-semibold text-[#101820] mb-1">Lawful basis</h3>
              <p>We rely on your <strong>consent</strong> (given at application) and our <strong>legitimate interests</strong> in
                assessing candidates and managing recruitment. You may withdraw consent at any time from your dashboard.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#101820] mb-1">Purpose limitation & data minimisation</h3>
              <p>Data is collected only for recruitment assessment and is limited to information relevant to the role. We do not
                use your data for automated final decision-making or unrelated marketing.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#101820] mb-1">Retention</h3>
              <p>Application data is retained for a configurable period after the recruitment process completes (default 12 months),
                or up to 24 months where you separately consent to be considered for future roles. These periods are configurable
                business settings and are not stated as legal requirements.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#101820] mb-2 flex items-center gap-2"><UserCheck className="h-4 w-4 text-[#C79A3B]" /> Your rights under GDPR</h3>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {RIGHTS.map((r) => (
                  <li key={r} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-[#2E7D32] shrink-0 mt-0.5" /> {r}</li>
                ))}
              </ul>
              <p className="mt-2">You can exercise access and erasure directly via <em>Download My Data</em> and <em>Request Profile Deletion</em> in your candidate dashboard.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#101820] mb-1 flex items-center gap-2"><Lock className="h-4 w-4 text-[#C79A3B]" /> Security</h3>
              <p>Passwords are securely hashed, CVs are stored in access-controlled storage and are never publicly accessible, and
                access to candidate data is role-based and audit-logged.</p>
            </div>
            <p className="text-xs text-muted-foreground border-t border-[#C79A3B]/20 pt-4">
              To exercise your rights or raise a data protection query, contact <a href="mailto:careers@kerafoods.ie" className="text-[#8a6a1e] hover:underline">careers@kerafoods.ie</a>.
              You also have the right to lodge a complaint with the Irish supervisory authority, the <strong>Data Protection
              Commission (DPC), Ireland</strong> (www.dataprotection.ie).
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
